/**
 * Visualização por Data de Publicação - JurisPrazo V2
 * Interface igual ao SEDEP - mostra comunicações agrupadas por data
 */

import React, { useState, useMemo } from 'react';
import { Calendar, ChevronDown, ChevronRight, Eye, EyeOff, FileText, Clock, AlertCircle } from 'lucide-react';

const DateFilterView = ({ comunicacoes, onComunicacaoSelect }) => {
  const [expandedDates, setExpandedDates] = useState(new Set());

  // Agrupar comunicações por data de publicação
  const comunicacoesPorData = useMemo(() => {
    const grupos = {};
    
    comunicacoes.forEach(com => {
      const dataPublicacao = com.data_disponibilizacao || com.dataPublicacao;
      if (!dataPublicacao) return;
      
      try {
        const data = new Date(dataPublicacao);
        const dataStr = data.toISOString().split('T')[0];
        const dataFormatada = data.toLocaleDateString('pt-BR', {
          weekday: 'long',
          year: 'numeric', 
          month: 'long',
          day: 'numeric'
        });
        
        if (!grupos[dataStr]) {
          grupos[dataStr] = {
            data: dataStr,
            dataFormatada: dataFormatada,
            comunicacoes: [],
            total: 0,
            naoLidas: 0,
            comPrazo: 0,
            urgentes: 0
          };
        }
        
        grupos[dataStr].comunicacoes.push(com);
        grupos[dataStr].total++;
        
        if (!com.isRead) grupos[dataStr].naoLidas++;
        if (com.temPrazo) grupos[dataStr].comPrazo++;
        if (com.urgencia === 'hoje' || com.urgencia === 'vencido') grupos[dataStr].urgentes++;
        
      } catch (error) {
        console.warn('⚠️ Data inválida:', dataPublicacao);
      }
    });
    
    // Converter para array e ordenar por data (mais recente primeiro)
    return Object.values(grupos).sort((a, b) => new Date(b.data) - new Date(a.data));
    
  }, [comunicacoes]);

  const toggleDate = (data) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(data)) {
      newExpanded.delete(data);
    } else {
      newExpanded.add(data);
    }
    setExpandedDates(newExpanded);
  };

  const expandAll = () => {
    const allDates = new Set(comunicacoesPorData.map(grupo => grupo.data));
    setExpandedDates(allDates);
  };

  const collapseAll = () => {
    setExpandedDates(new Set());
  };

  const getStatusIcon = (com) => {
    if (!com.isRead) return <EyeOff className="w-4 h-4 text-blue-600" />;
    if (com.urgencia === 'vencido') return <AlertCircle className="w-4 h-4 text-red-600" />;
    if (com.urgencia === 'hoje') return <Clock className="w-4 h-4 text-yellow-600" />;
    if (com.temPrazo) return <FileText className="w-4 h-4 text-green-600" />;
    return <Eye className="w-4 h-4 text-gray-500" />;
  };

  const getStatusClass = (com) => {
    if (!com.isRead) return 'bg-blue-50 border-l-4 border-l-blue-500';
    if (com.urgencia === 'vencido') return 'bg-red-50 border-l-4 border-l-red-500';
    if (com.urgencia === 'hoje') return 'bg-yellow-50 border-l-4 border-l-yellow-500';
    if (com.temPrazo) return 'bg-green-50 border-l-4 border-l-green-500';
    return 'bg-gray-50 border-l-4 border-l-gray-300';
  };

  if (comunicacoesPorData.length === 0) {
    return (
      <div className="text-center p-8">
        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma comunicação encontrada</h3>
        <p className="text-gray-500">Não há comunicações para exibir por data.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="font-medium text-blue-900">
            📅 Comunicações por Data de Publicação
          </h3>
          <span className="bg-blue-200 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
            {comunicacoesPorData.length} datas
          </span>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={expandAll}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 hover:bg-blue-200 rounded"
          >
            Expandir Todas
          </button>
          <button
            onClick={collapseAll}
            className="text-xs text-gray-600 hover:text-gray-800 font-medium px-2 py-1 hover:bg-gray-200 rounded"
          >
            Recolher Todas
          </button>
        </div>
      </div>

      {/* Lista de datas */}
      <div className="max-h-96 overflow-y-auto">
        {comunicacoesPorData.map((grupo) => (
          <div key={grupo.data} className="border-b border-gray-100 last:border-b-0">
            {/* Cabeçalho da data */}
            <div
              className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleDate(grupo.data)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {expandedDates.has(grupo.data) ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                  
                  <div>
                    <div className="font-medium text-gray-900 capitalize">
                      {grupo.dataFormatada}
                    </div>
                    <div className="text-xs text-gray-500">
                      {grupo.data}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Badges de status */}
                  <div className="flex gap-2">
                    {grupo.naoLidas > 0 && (
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                        {grupo.naoLidas} não lidas
                      </span>
                    )}
                    {grupo.urgentes > 0 && (
                      <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                        {grupo.urgentes} urgentes
                      </span>
                    )}
                    {grupo.comPrazo > 0 && (
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                        {grupo.comPrazo} com prazo
                      </span>
                    )}
                  </div>
                  
                  {/* Total */}
                  <span className="bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1 rounded-full">
                    {grupo.total}
                  </span>
                </div>
              </div>
            </div>

            {/* Lista de comunicações (expandida) */}
            {expandedDates.has(grupo.data) && (
              <div className="bg-gray-50 px-4 pb-4">
                {grupo.comunicacoes.map((com, index) => (
                  <div
                    key={com.id || index}
                    className={`mb-2 p-3 rounded-lg cursor-pointer hover:shadow-sm transition-all ${getStatusClass(com)}`}
                    onClick={() => onComunicacaoSelect && onComunicacaoSelect(com)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getStatusIcon(com)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        {/* Processo */}
                        <div className="font-medium text-sm text-gray-900 truncate">
                          📋 {com.numeroProcesso || 'Processo não identificado'}
                        </div>
                        
                        {/* Tribunal e Tipo */}
                        <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                          <span className="bg-gray-200 px-2 py-1 rounded">
                            🏛️ {com.siglaTribunal || 'Tribunal'}
                          </span>
                          {com.tipoAto && (
                            <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded">
                              {com.tipoAto}
                            </span>
                          )}
                        </div>
                        
                        {/* Conteúdo (preview) */}
                        {com.conteudo && (
                          <div className="text-xs text-gray-700 mt-2 line-clamp-2">
                            {com.conteudo.substring(0, 120)}...
                          </div>
                        )}
                        
                        {/* Horário */}
                        <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {com.data_disponibilizacao ? 
                            new Date(com.data_disponibilizacao).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Horário não disponível'
                          }
                        </div>
                      </div>
                      
                      {/* Badges de status */}
                      <div className="flex flex-col gap-1">
                        {!com.isRead && (
                          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full text-center">
                            Nova
                          </span>
                        )}
                        {com.temPrazo && (
                          <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full text-center">
                            Prazo
                          </span>
                        )}
                        {com.urgencia === 'vencido' && (
                          <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full text-center">
                            Vencido
                          </span>
                        )}
                        {com.urgencia === 'hoje' && (
                          <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full text-center">
                            Hoje
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer com resumo */}
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            📊 Total: {comunicacoes.length} comunicações em {comunicacoesPorData.length} datas
          </span>
          <span>
            👁️ {comunicacoes.filter(c => !c.isRead).length} não lidas
          </span>
        </div>
      </div>
    </div>
  );
};

export default DateFilterView;
